import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { QueenColorIcon } from '../components/hives/QueenColorIcon';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiariesApi, type Apiary } from '../services/apiaries';
import { hivesApi, type Hive, type HiveApiaryInfo, type HiveChild, type HiveLog, type HiveMetaItem, type HiveQuickUpdatePayload, type HiveSuperBoxEntry, type HiveSuperBoxProduct } from '../services/hives';

type Tab = 'overview' | 'logs' | 'apiary' | 'children';
type QuickField = HiveQuickUpdatePayload['field'];

type HiveForm = {
  name: string;
  hiveNo: string;
  hiveCode: string;
  hiveType: string;
  hiveFormatItemId: string;
  broodRoomTypeItemId: string;
  hiveStatusItemId: string;
  queenColor: string;
  queenStatusItemId: string;
  startedAt: string;
  endedAt: string;
  notes: string;
  siteNotes: string;
};

type LogForm = {
  logDate: string;
  eventType: string;
  isTreatment: boolean;
  preparationProductId: string;
  preparation: string;
  withdrawalDays: string;
  notes: string;
  createSplitHive: boolean;
  queenColor: string;
};

type SplitForm = {
  logDate: string;
  notes: string;
};

function todayDateValue() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function defaultLogForm(): LogForm {
  return {
    logDate: todayDateValue(),
    eventType: 'Allmän kommentar',
    isTreatment: false,
    preparationProductId: '',
    preparation: '',
    withdrawalDays: '',
    notes: '',
    createSplitHive: false,
    queenColor: '',
  };
}

function emptySplitForm(): SplitForm {
  return {
    logDate: todayDateValue(),
    notes: '',
  };
}

const QUEEN_COLOR_LOG_TYPES = new Set([
  'Isättning av parad drottning',
  'Isättning av oparad drottning',
  'Isättning av drottningcell',
  'Egenuppdragen, parad, drottning noterad',
]);

const TREATMENT_LOG_TYPES = new Set(['Varroabehandling']);
const PREPARATION_REMOVED_LOG_TYPES = new Set(['Preparat borttaget']);

const TRIGGER_CREATE_HIVE_LOG_TYPES = new Set([
  'Gör avläggare (walk-away)',
  'Gör konstgjord svärm',
  'Ny kupa - fångad svärm',
  'Ny kupa - köpt',
]);

function toLocalDateTime(dt?: string | null) {
  if (!dt) return '-';
  const d = new Date(dt.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? dt : d.toLocaleString('sv-SE');
}

function toLocalDate(dt?: string | null) {
  if (!dt) return '-';
  const d = new Date(dt.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? dt.slice(0, 10) : d.toLocaleDateString('sv-SE');
}

function toDateValue(dt?: string | null) {
  return dt ? dt.slice(0, 10) : '';
}

function hiveTitle(hive: Hive | null) {
  return !hive ? 'Kupa' : hive.hiveNo != null ? `Kupa #${hive.hiveNo}` : hive.name;
}

function text(v?: string | null) {
  return v && v.trim() !== '' ? v : '-';
}

function requiresQueenColor(eventType: string) {
  return QUEEN_COLOR_LOG_TYPES.has(eventType);
}

function isTreatmentLogType(eventType: string) {
  return TREATMENT_LOG_TYPES.has(eventType);
}

function isPreparationRemovedLogType(eventType: string) {
  return PREPARATION_REMOVED_LOG_TYPES.has(eventType);
}

function createsHiveFromLogType(eventType: string) {
  return TRIGGER_CREATE_HIVE_LOG_TYPES.has(eventType);
}

function splitStatusLabel(label: string | null | undefined): { short: string; long: string } {
  const raw = (label || '').trim();
  if (!raw) return { short: '-', long: '-' };
  const idx = raw.indexOf(' - ');
  if (idx > 0) {
    const short = raw.slice(0, idx).trim();
    const long = raw.slice(idx + 3).trim();
    return { short, long: long || short };
  }
  return { short: raw, long: raw };
}

function shortFormatLabel(label: string | null | undefined): string {
  const raw = (label || '').trim().toUpperCase();
  if (!raw) return '-';
  if (raw.includes('LN')) return 'LN';
  if (raw === 'D' || raw.includes('DADANT')) return 'D';
  if (raw === 'L' || raw.includes('LANG')) return 'L';
  return raw;
}

function formFromHive(hive: Hive): HiveForm {
  return {
    name: hive.name ?? '',
    hiveNo: hive.hiveNo != null ? String(hive.hiveNo) : '',
    hiveCode: hive.hiveCode ?? '',
    hiveType: hive.hiveType ?? '',
    hiveFormatItemId: hive.hiveFormatItemId != null ? String(hive.hiveFormatItemId) : '',
    broodRoomTypeItemId: hive.broodRoomTypeItemId != null ? String(hive.broodRoomTypeItemId) : '',
    hiveStatusItemId: hive.hiveStatusItemId != null ? String(hive.hiveStatusItemId) : '',
    queenColor: hive.queenColor ?? '',
    queenStatusItemId: hive.queenStatusItemId != null ? String(hive.queenStatusItemId) : '',
    startedAt: toDateValue(hive.startedAt),
    endedAt: toDateValue(hive.endedAt),
    notes: hive.hiveNotes ?? '',
    siteNotes: hive.siteNotes ?? '',
  };
}

function pill(active: boolean) {
  return active ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
}

export function HiveDetailPage() {
  const { id } = useParams();
  const hiveId = Number(id);
  const navigate = useNavigate();
  const toast = useToast();
  const { canWrite } = useAuth();
  const canWriteHives = canWrite('hives');

  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [savingLog, setSavingLog] = useState(false);
  const [savingSplit, setSavingSplit] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null);
  const [pendingDeleteLog, setPendingDeleteLog] = useState<HiveLog | null>(null);
  const [savingField, setSavingField] = useState<QuickField | null>(null);
  const [superBoxBusy, setSuperBoxBusy] = useState(false);
  const [showNewLogForm, setShowNewLogForm] = useState(false);
  const [showSplitForm, setShowSplitForm] = useState(false);
  const [hive, setHive] = useState<Hive | null>(null);
  const [hiveNavItems, setHiveNavItems] = useState<Hive[]>([]);
  const [hiveTodoDraft, setHiveTodoDraft] = useState('');
  const [savingHiveTodo, setSavingHiveTodo] = useState(false);
  const [apiary, setApiary] = useState<HiveApiaryInfo | null>(null);
  const [children, setChildren] = useState<HiveChild[]>([]);
  const [logs, setLogs] = useState<HiveLog[]>([]);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [form, setForm] = useState<HiveForm | null>(null);
  const [logForm, setLogForm] = useState<LogForm>(defaultLogForm);
  const [splitForm, setSplitForm] = useState<SplitForm>(emptySplitForm);
  const [hiveStatusItems, setHiveStatusItems] = useState<HiveMetaItem[]>([]);
  const [queenStatusItems, setQueenStatusItems] = useState<HiveMetaItem[]>([]);
  const [hiveFormatItems, setHiveFormatItems] = useState<HiveMetaItem[]>([]);
  const [broodRoomItems, setBroodRoomItems] = useState<HiveMetaItem[]>([]);
  const [hiveLogTypeItems, setHiveLogTypeItems] = useState<HiveMetaItem[]>([]);
  const [preparations, setPreparations] = useState<Array<{ id: number; sku: string | null; name: string; withdrawalDays: number | null; treatmentLengthDays: number | null; instructionUrl: string | null }>>([]);
  const [superBoxProducts, setSuperBoxProducts] = useState<HiveSuperBoxProduct[]>([]);
  const [installedSuperBoxes, setInstalledSuperBoxes] = useState<HiveSuperBoxEntry[]>([]);
  const [selectedSuperBoxKey, setSelectedSuperBoxKey] = useState<string | null>(null);
  const [apiaryOptions, setApiaryOptions] = useState<Apiary[]>([]);
  const [targetApiarySiteId, setTargetApiarySiteId] = useState<string>('');
  const [movingApiary, setMovingApiary] = useState(false);

  async function loadDetail(showSpinner = true) {
    if (!Number.isFinite(hiveId) || hiveId < 1) {
      toast.error('Ogiltig kupa');
      navigate('/hives', { replace: true });
      return;
    }
    if (showSpinner) {
      setLoading(true);
    }
    try {
      const [detailRes, metaRes] = await Promise.all([hivesApi.get(hiveId), hivesApi.meta()]);
      setHive(detailRes.hive);
      setApiary(detailRes.apiary);
      setChildren(detailRes.children);
      setForm(formFromHive(detailRes.hive));
      setHiveStatusItems(metaRes.lists.HIVE_STATUS ?? []);
      setQueenStatusItems(metaRes.lists.QUEEN_STATUS ?? []);
      setHiveFormatItems(metaRes.lists.HIVE_FORMAT ?? []);
      setBroodRoomItems(metaRes.lists.BROOD_ROOM_TYPES ?? []);
      setHiveLogTypeItems(metaRes.lists.HIVE_LOG_TYPES ?? []);
      setPreparations(metaRes.preparations ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda kupan');
      navigate('/hives', { replace: true });
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  }

  async function loadSuperBoxes() {
    try {
      const res = await hivesApi.superBoxes.list(hiveId);
      setSuperBoxProducts(res.availableProducts ?? []);
      setInstalledSuperBoxes(res.installedBoxes ?? []);
      setSelectedSuperBoxKey((current) => (current && res.installedBoxes.some((box) => box.entryKey === current) ? current : null));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda skattlådor');
    }
  }

  async function loadLogs() {
    try {
      const res = await hivesApi.logs.list(hiveId);
      setLogs(res.logs);
      setLogsLoaded(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda kuplogg');
    }
  }

  useEffect(() => {
    void loadDetail();
  }, [hiveId]);

  useEffect(() => {
    void loadSuperBoxes();
  }, [hiveId]);

  useEffect(() => {
    async function loadHiveNavigation() {
      try {
        const res = await hivesApi.list();
        setHiveNavItems(res.hives ?? []);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Kunde inte ladda kupnavigering');
      }
    }
    void loadHiveNavigation();
  }, [hiveId, toast]);

  useEffect(() => {
    async function loadApiaries() {
      try {
        const res = await apiariesApi.list();
        setApiaryOptions((res.apiaries ?? []).filter((item) => item.isActive === 1));
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Kunde inte ladda bigårdar');
      }
    }
    void loadApiaries();
  }, [toast]);

  useEffect(() => {
    setLogs([]);
    setLogsLoaded(false);
    setLogForm(defaultLogForm());
    setSplitForm(emptySplitForm());
    setShowNewLogForm(false);
    setShowSplitForm(false);
    setSelectedSuperBoxKey(null);
  }, [hiveId]);

  useEffect(() => {
    setTargetApiarySiteId(hive?.apiarySiteId != null ? String(hive.apiarySiteId) : '');
  }, [hive?.apiarySiteId]);

  useEffect(() => {
    setHiveTodoDraft(hive?.hiveTodoNotes ?? '');
  }, [hive?.hiveTodoNotes]);

  useEffect(() => {
    if (tab === 'logs' && !logsLoaded) {
      void loadLogs();
    }
  }, [tab, logsLoaded, hiveId]);

  const childCount = children.length;
  const activeStatusLabel = useMemo(() => hiveStatusItems.find((x) => String(x.id) === form?.hiveStatusItemId)?.label ?? hive?.hiveStatusLabel ?? hive?.hiveStatus ?? '-', [form?.hiveStatusItemId, hive?.hiveStatus, hive?.hiveStatusLabel, hiveStatusItems]);
  const visibleHiveFormatItems = useMemo(
    () =>
      hiveFormatItems.filter((item) => {
        const short = shortFormatLabel(item.label);
        return short === 'LN' || short === 'D' || short === 'L';
      }),
    [hiveFormatItems],
  );
  const selectedLogType = logForm.eventType.trim();
  const showTreatmentFields = logForm.isTreatment || isTreatmentLogType(selectedLogType);
  const showPreparationRemovedFields = isPreparationRemovedLogType(selectedLogType);
  const showCreateHiveFields = logForm.createSplitHive || createsHiveFromLogType(selectedLogType);
  const showQueenColorField = requiresQueenColor(selectedLogType);
  const selectedSuperBox = useMemo(
    () => installedSuperBoxes.find((box) => box.entryKey === selectedSuperBoxKey) ?? null,
    [installedSuperBoxes, selectedSuperBoxKey],
  );
  const hiveNavIndex = useMemo(() => hiveNavItems.findIndex((item) => item.id === hiveId), [hiveNavItems, hiveId]);
  const previousHive = hiveNavIndex > 0 ? hiveNavItems[hiveNavIndex - 1] : null;
  const nextHive = hiveNavIndex >= 0 && hiveNavIndex < hiveNavItems.length - 1 ? hiveNavItems[hiveNavIndex + 1] : null;

  async function applyQuickUpdate(field: QuickField, value: number | string | null) {
    if (!hive || !form || !canWriteHives) return;
    setSavingField(field);
    try {
      const res = await hivesApi.quickUpdate(hive.id, { field, value });
      if (res.changed) {
        if (field === 'queenColor') {
          setForm((current) => (current ? { ...current, queenColor: typeof value === 'string' ? value : '' } : current));
        } else {
          const normalized = value === null || value === '' ? '' : String(value);
          setForm((current) => (current ? { ...current, [field]: normalized } : current));
        }
        if (res.message) {
          toast.success(res.message);
        } else {
          toast.success('Kupan uppdaterad');
        }
        await Promise.all([loadDetail(false), loadSuperBoxes(), logsLoaded ? loadLogs() : Promise.resolve()]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara ändringen');
    } finally {
      setSavingField(null);
    }
  }

  async function addSuperBox(productId: number) {
    if (!hive || !canWriteHives) return;
    setSuperBoxBusy(true);
    try {
      await hivesApi.superBoxes.add(hive.id, productId);
      toast.success('Skattlåda satt på kupan');
      await Promise.all([loadSuperBoxes(), loadDetail(false), logsLoaded ? loadLogs() : Promise.resolve()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte sätta på skattlåda');
    } finally {
      setSuperBoxBusy(false);
    }
  }

  async function removeSuperBox(entryKey: string) {
    if (!hive || !canWriteHives) return;
    setSuperBoxBusy(true);
    try {
      await hivesApi.superBoxes.remove(hive.id, entryKey);
      toast.success('Skattlådan är skattad');
      setSelectedSuperBoxKey(null);
      await Promise.all([loadSuperBoxes(), loadDetail(false), logsLoaded ? loadLogs() : Promise.resolve()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte skatta skattlådan');
    } finally {
      setSuperBoxBusy(false);
    }
  }

  async function moveToApiary() {
    if (!hive || !canWriteHives) return;
    const nextApiarySiteId = Number(targetApiarySiteId);
    if (!Number.isFinite(nextApiarySiteId) || nextApiarySiteId < 1) {
      toast.error('Välj en bigård');
      return;
    }

    setMovingApiary(true);
    try {
      const res = await hivesApi.moveApiary(hive.id, { apiarySiteId: nextApiarySiteId });
      if (res.message) {
        toast.success(res.message);
      } else {
        toast.success('Kupan flyttades');
      }
      await Promise.all([loadDetail(false), logsLoaded ? loadLogs() : Promise.resolve()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte flytta kupan');
    } finally {
      setMovingApiary(false);
    }
  }

  async function saveLog() {
    if (!hive) return;
    if (!logForm.eventType.trim()) return toast.error('Loggtyp krävs');
    setSavingLog(true);
    try {
      const res = await hivesApi.logs.create(hive.id, {
        logDate: logForm.logDate ? `${logForm.logDate} 12:00:00` : undefined,
        eventType: logForm.eventType.trim(),
        isTreatment: showTreatmentFields,
        preparationProductId: (showTreatmentFields || showPreparationRemovedFields) && logForm.preparationProductId ? Number(logForm.preparationProductId) : undefined,
        preparation: showTreatmentFields || showPreparationRemovedFields ? logForm.preparation.trim() || undefined : undefined,
        withdrawalDays: showTreatmentFields && logForm.withdrawalDays ? Number(logForm.withdrawalDays) : undefined,
        notes: logForm.notes.trim() || undefined,
        createSplitHive: showCreateHiveFields,
        queenColor: showQueenColorField ? logForm.queenColor.trim() || undefined : undefined,
      });
      setLogForm(defaultLogForm());
      setShowNewLogForm(false);
      toast.success(res.triggerCreatedHiveSiteId ? 'Kuplogg sparad och avläggare skapad' : 'Kuplogg sparad');
      await Promise.all([loadLogs(), loadDetail()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara kuplogg');
    } finally {
      setSavingLog(false);
    }
  }

  async function createSplitFromCurrentHive() {
    if (!hive || !canWriteHives) return;
    setSavingSplit(true);
    try {
      const res = await hivesApi.logs.create(hive.id, {
        logDate: splitForm.logDate ? `${splitForm.logDate} 12:00:00` : undefined,
        eventType: 'Gör avläggare (walk-away)',
        notes: splitForm.notes.trim() || undefined,
        createSplitHive: true,
      });
      setSplitForm(emptySplitForm());
      setShowSplitForm(false);
      toast.success(res.triggerCreatedHiveSiteId ? 'Avläggare skapad' : 'Logg sparad');
      await Promise.all([loadDetail(false), logsLoaded ? loadLogs() : Promise.resolve()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte skapa avläggare');
    } finally {
      setSavingSplit(false);
    }
  }

  async function deleteLog(logId: number) {
    if (!hive || !canWriteHives || deletingLogId !== null) return;
    setDeletingLogId(logId);
    try {
      await hivesApi.logs.remove(hive.id, logId);
      setPendingDeleteLog(null);
      toast.success('Kuplogg raderad');
      await Promise.all([loadLogs(), loadDetail(false)]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte radera kuplogg');
    } finally {
      setDeletingLogId(null);
    }
  }

  async function saveHiveTodo() {
    if (!hive || !canWriteHives || savingHiveTodo) return;
    setSavingHiveTodo(true);
    try {
      const res = await hivesApi.update(hive.id, { todoNotes: hiveTodoDraft.trim() });
      if (!res.ok) {
        throw new Error('Kunde inte spara att göra');
      }
      await loadDetail(false);
      toast.success('Att göra sparad');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara att göra');
    } finally {
      setSavingHiveTodo(false);
    }
  }

  if (loading || !hive || !form) return <div className="text-sm text-gray-500">Laddar kupa...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button type="button" className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-primary" onClick={() => navigate('/hives')}>
          <span aria-hidden="true">←</span><span>Tillbaka</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-lg text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => previousHive && navigate(`/hives/${previousHive.id}`)}
            disabled={!previousHive}
            aria-label={previousHive ? `Föregående kupa ${previousHive.hiveNo ?? previousHive.name}` : 'Ingen föregående kupa'}
            title={previousHive ? `Kupa ${previousHive.hiveNo ?? previousHive.name}` : 'Ingen föregående kupa'}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-lg text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => nextHive && navigate(`/hives/${nextHive.id}`)}
            disabled={!nextHive}
            aria-label={nextHive ? `Nästa kupa ${nextHive.hiveNo ?? nextHive.name}` : 'Ingen nästa kupa'}
            title={nextHive ? `Kupa ${nextHive.hiveNo ?? nextHive.name}` : 'Ingen nästa kupa'}
          >
            <span aria-hidden="true">›</span>
          </button>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-semibold text-gray-900">{hiveTitle(hive)}</h1><div className="mt-1 text-sm text-gray-600">{hive.apiaryName || 'Ingen bigård'}</div></div>
        <div className="pt-1 text-sm font-medium text-gray-600">{form.startedAt || '-'}</div>
      </div>
      <div className="rounded-2xl border border-gray-300 bg-white p-2">
        <div className="grid grid-cols-4 gap-1 sm:flex sm:flex-wrap sm:gap-2">
          {[
            ['overview', 'Kupan'], ['logs', 'Loggen'], ['apiary', 'Bigården'], ['children', `Avläggare (${childCount})`],
          ].map(([value, label]) => (
            <button key={value} type="button" onClick={() => setTab(value as Tab)} className={`min-w-0 rounded-xl px-2 py-2 text-center text-xs font-medium sm:px-4 sm:text-sm ${tab === value ? 'bg-white shadow-sm ring-1 ring-gray-200' : 'text-gray-700 hover:bg-gray-50'}`}>{label}</button>
          ))}
        </div>
      </div>
      {tab === 'overview' && (
        <div className="space-y-4 rounded-2xl border border-gray-300 bg-white p-4">
          <div className="space-y-2"><div className="text-sm font-semibold text-gray-900">Kupformat:</div><div className="flex flex-wrap gap-2">{visibleHiveFormatItems.map((item) => <button key={item.id} type="button" className={`rounded-xl border px-4 py-2 text-sm font-semibold ${pill(form.hiveFormatItemId === String(item.id))}`} onClick={() => void applyQuickUpdate('hiveFormatItemId', item.id)} disabled={!canWriteHives || savingField !== null}>{shortFormatLabel(item.label)}</button>)}</div></div>
          <div className="space-y-2"><div className="text-sm font-semibold text-gray-900">Yngelrum:</div><div className="flex flex-wrap gap-2">{broodRoomItems.map((item) => <button key={item.id} type="button" className={`rounded-xl border px-4 py-2 text-sm font-semibold ${pill(form.broodRoomTypeItemId === String(item.id))}`} onClick={() => void applyQuickUpdate('broodRoomTypeItemId', item.id)} disabled={!canWriteHives || savingField !== null}>{item.label}</button>)}</div></div>
          <div className="space-y-2"><div className="text-sm text-gray-900"><span className="font-semibold">Kupstatus:</span> <span>{splitStatusLabel(activeStatusLabel).long}</span></div><div className="flex flex-wrap gap-2">{hiveStatusItems.map((item) => <button key={item.id} type="button" className={`rounded-xl border px-4 py-2 text-sm font-semibold ${pill(form.hiveStatusItemId === String(item.id))}`} onClick={() => void applyQuickUpdate('hiveStatusItemId', item.id)} disabled={!canWriteHives || savingField !== null}>{splitStatusLabel(item.label).short}</button>)}</div></div>
          <div className="space-y-2"><div className="text-sm font-semibold text-gray-900">Aktuell drottningsfärg:</div><div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50"><QueenColorIcon color={form.queenColor || hive.queenColor} label={form.queenColor || hive.queenColor || undefined} /></div><select className="w-full max-w-[180px]" value={form.queenColor} onChange={(e) => void applyQuickUpdate('queenColor', e.target.value)} disabled={!canWriteHives || savingField !== null}><option value="">Ändra färg</option>{['vit', 'gul', 'röd', 'grön', 'blå'].map((color) => <option key={color} value={color}>{color}</option>)}</select></div></div>
          <div className="space-y-2"><div className="text-sm font-semibold text-gray-900">Drottningstatus:</div><div className="flex flex-wrap gap-2">{queenStatusItems.map((item) => <button key={item.id} type="button" className={`rounded-xl border px-4 py-2 text-sm font-semibold ${pill(form.queenStatusItemId === String(item.id))}`} onClick={() => void applyQuickUpdate('queenStatusItemId', item.id)} disabled={!canWriteHives || savingField !== null}>{item.label}</button>)}</div></div>
          {hive.currentQueenBatchId ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs uppercase tracking-wide text-amber-700">Drottningsursprung</div>
              <div className="mt-2 text-lg font-semibold text-gray-900">{hive.currentQueenBatchCode || '-'}</div>
              <div className="mt-1 text-sm text-gray-700">
                {hive.currentQueenMotherHiveSiteId ? (
                  <Link to={`/hives/${hive.currentQueenMotherHiveSiteId}`} className="font-medium text-primary hover:underline">
                    {hive.currentQueenMotherHiveNo != null ? `Kupa #${hive.currentQueenMotherHiveNo}` : (hive.currentQueenMotherHiveName || 'Modersamhälle')}
                  </Link>
                ) : (
                  <span>{hive.currentQueenMotherHiveName || 'Modersamhälle'}</span>
                )}
                {hive.currentQueenMotherApiaryName ? <span>{` · ${hive.currentQueenMotherApiaryName}`}</span> : null}
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {`Larvad ${toLocalDate(hive.currentQueenBatchGraftedAt)} · insatt ${toLocalDateTime(hive.currentQueenAssignedAt)}`}
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2"><div className="rounded-xl border border-gray-200 p-3"><div className="text-xs uppercase tracking-wide text-gray-500">Senaste behandling</div><div className="mt-1 text-sm font-medium text-gray-900">{text(hive.lastTreatmentPreparation)}</div><div className="mt-1 text-sm text-gray-600">{toLocalDateTime(hive.lastTreatmentAt)}</div></div><div className="rounded-xl border border-gray-200 p-3"><div className="text-xs uppercase tracking-wide text-gray-500">Karenstid t.o.m.</div><div className="mt-1 text-sm font-medium text-gray-900">{toLocalDateTime(hive.withdrawalUntil)}</div></div></div>
          <div className="rounded-xl border border-gray-200 p-3">
            <div className="text-sm font-semibold text-gray-900">Att göra</div>
            <div className="mt-1 text-sm text-gray-500">Anteckningar för sådant du ska fixa i eller ta med till just den här kupan.</div>
            {canWriteHives ? (
              <div className="mt-3 space-y-3">
                <textarea
                  className="min-h-[120px] w-full"
                  placeholder="Skriv saker att komma ihåg för kupan"
                  value={hiveTodoDraft}
                  onChange={(e) => setHiveTodoDraft(e.target.value)}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="rounded-lg bg-success px-4 py-2 text-sm font-medium text-white hover:bg-success-hover disabled:opacity-50"
                    onClick={() => void saveHiveTodo()}
                    disabled={savingHiveTodo}
                  >
                    {savingHiveTodo ? 'Sparar...' : 'Spara att göra'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 whitespace-pre-wrap text-sm font-medium text-gray-900">{text(hive.hiveTodoNotes)}</div>
            )}
          </div>
          <div className="space-y-3 rounded-xl border border-gray-200 p-3">
            <div className="text-sm font-semibold text-gray-900">Sätt på skattlåda</div>
            {superBoxProducts.length === 0 ? (
              <div className="text-sm text-gray-500">Inga produkter med namn som innehåller skattlåda hittades.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {superBoxProducts.map((product) => (
                  <button key={product.id} type="button" className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50" onClick={() => void addSuperBox(product.id)} disabled={!canWriteHives || superBoxBusy || savingField !== null}>
                    {product.name}
                  </button>
                ))}
              </div>
            )}
            <div className="text-sm font-semibold text-gray-900">Skattlådor på kupan just nu</div>
            {installedSuperBoxes.length === 0 ? (
              <div className="text-sm text-gray-500">Inga skattlådor på kupan.</div>
            ) : (
              <div className="space-y-2">
                {installedSuperBoxes.map((box) => (
                  <button key={box.entryKey} type="button" className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${selectedSuperBoxKey === box.entryKey ? 'border-primary bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`} onClick={() => setSelectedSuperBoxKey((current) => (current === box.entryKey ? null : box.entryKey))}>
                    <span className="font-medium text-gray-900">{box.productName}</span>
                    <span className="text-gray-500">{toLocalDate(box.movedAt)}</span>
                  </button>
                ))}
                {selectedSuperBox && (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
                    <div className="text-sm font-medium text-gray-900">Vill du skatta denna låda?</div>
                    <div className="mt-1 text-sm text-gray-600">{selectedSuperBox.productName} · påsatt {toLocalDate(selectedSuperBox.movedAt)}</div>
                    <div className="mt-3 flex gap-2">
                      <button type="button" className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50" onClick={() => void removeSuperBox(selectedSuperBox.entryKey)} disabled={superBoxBusy}>
                        {superBoxBusy ? 'Skattar...' : 'Skatta denna låda'}
                      </button>
                      <button type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setSelectedSuperBoxKey(null)} disabled={superBoxBusy}>
                        Avbryt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {tab === 'logs' && (
        <div className="space-y-4">
          {canWriteHives && showNewLogForm && <div className="rounded-2xl border border-gray-300 bg-white p-4">
            <div className="flex items-start justify-between gap-3"><h2 className="text-2xl font-semibold text-gray-900">Ny loggrad</h2><button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-lg text-gray-600 hover:bg-gray-50" onClick={() => setShowNewLogForm(false)} aria-label="Stäng ny loggrad">×</button></div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr_2fr_auto]"><label className="text-sm text-gray-700">Datum<input className="mt-1 w-full" type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={toDateValue(logForm.logDate)} onChange={(e) => setLogForm((v) => ({ ...v, logDate: e.target.value }))} /></label><label className="text-sm text-gray-700">Typ<select className="mt-1 w-full" value={logForm.eventType} onChange={(e) => setLogForm((v) => ({ ...v, eventType: e.target.value, isTreatment: isTreatmentLogType(e.target.value), createSplitHive: createsHiveFromLogType(e.target.value) }))}><option value="">(välj loggtyp)</option>{hiveLogTypeItems.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}</select></label><label className="text-sm text-gray-700">Kommentar<input className="mt-1 w-full" value={logForm.notes} onChange={(e) => setLogForm((v) => ({ ...v, notes: e.target.value }))} /></label><div className="flex items-end"><button type="button" className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50" onClick={saveLog} disabled={savingLog}>{savingLog ? 'Sparar...' : 'Spara'}</button></div></div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
              {!isTreatmentLogType(selectedLogType) && <label className="inline-flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={logForm.isTreatment} onChange={(e) => setLogForm((v) => ({ ...v, isTreatment: e.target.checked }))} />Detta är en behandling</label>}
              {!createsHiveFromLogType(selectedLogType) && <label className="inline-flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={logForm.createSplitHive} onChange={(e) => setLogForm((v) => ({ ...v, createSplitHive: e.target.checked }))} />Skapa avläggare</label>}
              {showQueenColorField && <select className="w-full" value={logForm.queenColor} onChange={(e) => setLogForm((v) => ({ ...v, queenColor: e.target.value }))}><option value="">Välj drottningfärg</option>{['vit', 'gul', 'röd', 'grön', 'blå'].map((color) => <option key={color} value={color}>{color}</option>)}</select>}
              {(showTreatmentFields || showPreparationRemovedFields) && <><select className="w-full" value={logForm.preparationProductId} onChange={(e) => { const selected = preparations.find((item) => item.id === Number(e.target.value)); setLogForm((v) => ({ ...v, preparationProductId: e.target.value, preparation: selected?.name ?? '', withdrawalDays: selected?.withdrawalDays != null ? String(selected.withdrawalDays) : v.withdrawalDays })); }}><option value="">Välj preparat</option>{preparations.map((preparation) => <option key={preparation.id} value={preparation.id}>{preparation.sku ? `${preparation.sku} - ` : ''}{preparation.name}</option>)}</select>{showTreatmentFields && <input className="w-full" type="number" min={0} placeholder="Karenstid (dagar)" value={logForm.withdrawalDays} onChange={(e) => setLogForm((v) => ({ ...v, withdrawalDays: e.target.value }))} />}</>}
            </div>
          </div>}
          <div className="rounded-2xl border border-gray-300 bg-white">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3"><div className="text-2xl font-semibold text-gray-900">Loggar</div>{canWriteHives && !showNewLogForm && <button type="button" className="rounded-lg bg-success px-3 py-2 text-sm font-medium text-white hover:bg-success-hover" onClick={() => { setLogForm(defaultLogForm()); setShowNewLogForm(true); }}>Ny logg</button>}</div>
            <TableWrapper><table className="w-full border-collapse text-sm"><TableHead><tr><th className="px-3 py-2 text-left font-medium">Datum</th><th className="px-3 py-2 text-left font-medium">Typ</th><th className="px-3 py-2 text-left font-medium">Kommentar</th><th className="px-3 py-2 text-left font-medium">Preparat</th><th className="px-3 py-2 text-left font-medium">Trigger</th>{canWriteHives ? <th className="px-3 py-2 text-right font-medium">Åtgärd</th> : null}</tr></TableHead><tbody>
              {!logsLoaded ? <tr><td className="px-3 py-3 text-gray-500" colSpan={canWriteHives ? 6 : 5}>Laddar loggar...</td></tr> : logs.length === 0 ? <tr><td className="px-3 py-3 text-gray-500" colSpan={canWriteHives ? 6 : 5}>Inga loggar ännu.</td></tr> : logs.map((log) => <tr key={log.id} className="border-t border-gray-100 align-top"><td className="px-3 py-3">{toLocalDate(log.logDate)}</td><td className="px-3 py-3 font-medium text-gray-900">{log.eventType}</td><td className="px-3 py-3">{text(log.notes)}</td><td className="px-3 py-3">{log.isTreatment ? `${log.preparation || '-'}${log.withdrawalDays != null ? ` · Karenstid ${log.withdrawalDays} dagar` : ''}` : log.eventType === 'Preparat borttaget' ? (log.preparation || '-') : '-'}</td><td className="px-3 py-3">{log.triggerCreatedHiveSiteId ? <Link className="text-primary hover:underline" to={`/hives/${log.triggerCreatedHiveSiteId}`}>{log.triggerCreatedHiveName || `Kupa #${log.triggerCreatedHiveSiteId}`}</Link> : '-'}</td>{canWriteHives ? <td className="px-3 py-3 text-right"><button type="button" className="rounded-lg border border-danger px-2 py-1 text-xs font-medium text-danger hover:bg-red-50 disabled:opacity-50" onClick={() => setPendingDeleteLog(log)} disabled={deletingLogId !== null}>{deletingLogId === log.id ? 'Raderar...' : 'Radera'}</button></td> : null}</tr>)}
            </tbody></table></TableWrapper>
          </div>
          {canWriteHives && pendingDeleteLog ? <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 px-4 py-6"><div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl"><div className="text-lg font-semibold text-gray-900">Radera loggrad?</div><div className="mt-2 text-sm text-gray-600">{pendingDeleteLog.eventType} · {toLocalDate(pendingDeleteLog.logDate)}</div><div className="mt-1 text-sm text-gray-500">Detta går inte att ångra.</div><div className="mt-4 flex justify-end gap-2"><button type="button" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setPendingDeleteLog(null)} disabled={deletingLogId !== null}>Avbryt</button><button type="button" className="rounded-lg bg-danger px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50" onClick={() => void deleteLog(pendingDeleteLog.id)} disabled={deletingLogId !== null}>{deletingLogId === pendingDeleteLog.id ? 'Raderar...' : 'Ja, radera'}</button></div></div></div> : null}
        </div>
      )}
      {tab === 'apiary' && (
        <div className="rounded-2xl border border-gray-300 bg-white p-4">
          <h2 className="text-2xl font-semibold text-gray-900">Bigården</h2>
          <div className="mt-4 grid grid-cols-1 gap-y-4 md:grid-cols-[220px_1fr] md:gap-x-6">
            <div className="text-sm text-gray-600">Namn</div><div className="text-sm font-medium text-gray-900">{text(apiary?.name)}</div>
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-600">Adress</div><div className="border-t border-gray-200 pt-4 text-sm font-medium text-gray-900">{[apiary?.address1, apiary?.address2, [apiary?.postalCode, apiary?.city].filter(Boolean).join(' '), apiary?.country].filter(Boolean).join(', ') || '-'}</div>
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-600">Markägare</div><div className="border-t border-gray-200 pt-4 text-sm font-medium text-gray-900">{text(apiary?.markagare)}</div>
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-600">Kontakt</div><div className="border-t border-gray-200 pt-4 text-sm font-medium text-gray-900">{text(apiary?.kontaktinfo)}</div>
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-600">Position</div><div className="border-t border-gray-200 pt-4 text-sm font-medium text-gray-900">{apiary?.lat && apiary?.lng ? `${apiary.lat}, ${apiary.lng}` : '-'}</div>
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-600">Kommentar</div><div className="border-t border-gray-200 pt-4 whitespace-pre-wrap text-sm font-medium text-gray-900">{text(apiary?.notes)}</div>
          </div>
          {canWriteHives && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="text-sm font-semibold text-gray-900">Flytta till ny bigård</div>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="flex-1 text-sm text-gray-700">
                  <select className="mt-1 w-full" value={targetApiarySiteId} onChange={(e) => setTargetApiarySiteId(e.target.value)} disabled={movingApiary}>
                    <option value="">Välj bigård</option>
                    {apiaryOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="button" className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50" onClick={() => void moveToApiary()} disabled={!targetApiarySiteId || movingApiary || targetApiarySiteId === String(hive.apiarySiteId ?? '')}>
                  {movingApiary ? 'Flyttar...' : 'Flytta'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {tab === 'children' && (
        <div className="rounded-2xl border border-gray-300 bg-white">
          <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-2xl font-semibold text-gray-900">Avläggare (barn till denna kupa)</div>
            {canWriteHives && (
              <button
                type="button"
                className="rounded-lg bg-success px-3 py-2 text-sm font-medium text-white hover:bg-success-hover disabled:opacity-50"
                onClick={() => setShowSplitForm((current) => !current)}
                disabled={savingSplit}
              >
                {showSplitForm ? 'Stäng' : 'Gör avläggare från denna kupa'}
              </button>
            )}
          </div>
          {canWriteHives && showSplitForm ? (
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr_auto]">
                <label className="text-sm text-gray-700">
                  Datum
                  <input
                    className="mt-1 w-full"
                    type="text"
                    inputMode="numeric"
                    placeholder="ÅÅÅÅ-MM-DD"
                    value={splitForm.logDate}
                    onChange={(e) => setSplitForm((current) => ({ ...current, logDate: e.target.value }))}
                    disabled={savingSplit}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Kommentar
                  <input
                    className="mt-1 w-full"
                    value={splitForm.notes}
                    onChange={(e) => setSplitForm((current) => ({ ...current, notes: e.target.value }))}
                    disabled={savingSplit}
                    placeholder="T.ex. låda 2"
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
                    onClick={() => void createSplitFromCurrentHive()}
                    disabled={savingSplit}
                  >
                    {savingSplit ? 'Skapar...' : 'Skapa'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          <TableWrapper><table className="w-full border-collapse text-sm"><TableHead><tr><th className="px-3 py-2 text-left font-medium">Kupa</th><th className="px-3 py-2 text-left font-medium">Typ</th><th className="px-3 py-2 text-left font-medium">Skapad</th><th className="px-3 py-2 text-left font-medium">Bigård</th><th className="px-3 py-2 text-left font-medium">Färg</th></tr></TableHead><tbody>
            {children.length === 0 ? <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>Inga avläggare ännu.</td></tr> : children.map((child) => <tr key={child.id} className="border-t border-gray-100"><td className="px-3 py-3"><Link className="text-primary hover:underline" to={`/hives/${child.id}`}>{child.hiveNo != null ? `#${child.hiveNo}` : child.name}</Link></td><td className="px-3 py-3">{text(child.hiveType)}</td><td className="px-3 py-3">{toLocalDateTime(child.startedAt)}</td><td className="px-3 py-3">{text(child.apiaryName)}</td><td className="px-3 py-3"><div className="inline-flex items-center gap-2"><QueenColorIcon color={child.queenColor} /><span>{text(child.queenColor)}</span></div></td></tr>)}
          </tbody></table></TableWrapper>
        </div>
      )}
    </div>
  );
}
